'use client';

import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { useThemePrefsStore } from '@/shared/stores/theme-prefs-store';
import { useFontPrefsStore } from '@/shared/stores/font-prefs-store';
import { useFormatPrefsStore } from '@/shared/stores/format-prefs-store';
import { appearancePrefsApi, type AppearancePrefsBlob } from '@/shared/services/appearance-prefs-api';

/**
 * Sincronizează preferințele de aspect (temă + tipografie) între localStorage
 * (Zustand persist) și DB (`User.appearancePrefs`) pentru a permite cross-device.
 *
 * Comportament:
 *   1. **Hydrate**: după login (când userId apare), face GET /auth/appearance-prefs.
 *      Dacă DB are prefs salvate, le aplică peste Zustand stores.
 *      Dacă DB nu are nimic, push-uiește valorile localStorage curente la DB.
 *   2. **Watch & Push**: subscribe la modificările Zustand stores. La orice
 *      schimbare, debounce 600ms și PATCH /auth/appearance-prefs.
 *
 * Conflict: last-write-wins (timestamp implicit prin requestul HTTP).
 * localStorage continuă să fie sursa de adevăr între sesiuni; DB e doar oglindă.
 */
export function AppearancePrefsSync() {
  const userId = useAuthStore((s) => s.user?.id);
  const hydratedRef = useRef(false);

  // ─── Hydrate la login ────────────────────────────────────────
  useEffect(() => {
    if (!userId) {
      hydratedRef.current = false;
      return;
    }
    if (hydratedRef.current) return;

    let cancelled = false;
    (async () => {
      try {
        const remote = await appearancePrefsApi.get();
        if (cancelled) return;

        if (remote && (remote.themePrefs || remote.fontPrefs || remote.formatPrefs)) {
          // DB are prefs — aplică peste Zustand
          if (remote.themePrefs) {
            useThemePrefsStore.setState(remote.themePrefs);
          }
          if (remote.fontPrefs) {
            useFontPrefsStore.setState(remote.fontPrefs);
          }
          if (remote.formatPrefs?.numberFormat) {
            useFormatPrefsStore.setState({ numberFormat: remote.formatPrefs.numberFormat });
          }
        } else {
          // DB e gol — push valorile localStorage la DB (first-time sync)
          const themePrefs = pickThemePrefs(useThemePrefsStore.getState());
          const fontPrefs = pickFontPrefs(useFontPrefsStore.getState());
          const formatPrefs = pickFormatPrefs(useFormatPrefsStore.getState());
          await appearancePrefsApi.update({ themePrefs, fontPrefs, formatPrefs }).catch(() => {});
        }
      } catch {
        // Silent fail — localStorage continuă să funcționeze
      } finally {
        if (!cancelled) hydratedRef.current = true;
      }
    })();

    return () => { cancelled = true; };
  }, [userId]);

  // ─── Watch & Push (debounced) ────────────────────────────────
  useEffect(() => {
    if (!userId) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let lastSerialized = '';

    const push = () => {
      if (!hydratedRef.current) return; // evităm să PATCH-uim înainte să fim siguri ce e în DB
      const themePrefs = pickThemePrefs(useThemePrefsStore.getState());
      const fontPrefs = pickFontPrefs(useFontPrefsStore.getState());
      const formatPrefs = pickFormatPrefs(useFormatPrefsStore.getState());
      const blob: AppearancePrefsBlob = { themePrefs, fontPrefs, formatPrefs };
      const serialized = JSON.stringify(blob);
      if (serialized === lastSerialized) return;
      lastSerialized = serialized;
      appearancePrefsApi.update(blob).catch(() => {});
    };

    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(push, 600);
    };

    const unsubTheme = useThemePrefsStore.subscribe(schedule);
    const unsubFont = useFontPrefsStore.subscribe(schedule);
    const unsubFormat = useFormatPrefsStore.subscribe(schedule);

    return () => {
      unsubTheme();
      unsubFont();
      unsubFormat();
      if (timer) clearTimeout(timer);
    };
  }, [userId]);

  return null;
}

// ─── Helpers — păstrăm doar câmpurile persistate ────────────────
function pickThemePrefs(s: any) {
  return {
    presetId: s.presetId,
    customHex: s.customHex,
    customOriginalHex: s.customOriginalHex,
    scale: s.scale,
    headerStyle: s.headerStyle,
    bgTone: s.bgTone,
    cornerStyle: s.cornerStyle,
    tableHeaderStyle: s.tableHeaderStyle,
  };
}
function pickFontPrefs(s: any) {
  return {
    fontFamily: s.fontFamily,
    fontSize: s.fontSize,
  };
}
function pickFormatPrefs(s: any) {
  return { numberFormat: s.numberFormat };
}
